import { useEffect, useState } from "react";
import {
  Save,
  Trash2,
  Plus,
  AlertTriangle,
  Loader2,
  User as UserIcon,
  Monitor,
  Mail as MailIcon,
  CalendarClock,
  Cpu,
  Plug,
  ShieldCheck,
  Image as ImageIcon,
  Sun,
  Moon,
  LogOut,
  Check,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { api, ApiError } from "../api";
import { Button } from "../ui";
import { useToast } from "../ui/toast";
import { useTheme, PRESETS, Mode, Preset, CustomTheme, CUSTOM_DEFAULT } from "../ui/theme";
import { useFontScale, FONT_SCALE_OPTIONS, FontScale } from "../ui/fontScale";
import {
  useUiFont,
  UI_FONT_OPTIONS,
  UI_FONT_STACKS,
  X_HOEHEN_ANKER,
  fontInstalliert,
  UiFont,
} from "../ui/uiFont";
import {
  useSourcesPref,
  SOURCES_DEFAULT_OPTIONS,
  SourcesDefaultState,
} from "../ui/sourcesPref";
import { useTaskViewPref, TASK_VIEW_OPTIONS } from "../ui/taskViewPref";
import {
  useInboxDepth,
  INBOX_DEPTH_OPTIONS,
  InboxDepth,
} from "../ui/mailInboxDepth";
import { useAutoMarkRead } from "../ui/mailAutoMarkRead";
import {
  useBusinessHours,
  START_HOUR_CHOICES,
  END_HOUR_CHOICES,
  hourLabel,
} from "../ui/calendarBusinessHours";
import { useAuth } from "../auth";
import { katalog, useLang, useT, type Lang } from "../i18n";
import { useIsAdmin } from "../studioAccess";

interface ModelInfo {
  id: string;
  label: string;
  provider: string;
  in_per_m_usd: number;
  out_per_m_usd: number;
}
interface UseCaseInfo {
  key: string;
  label: string;
  default_model: string;
}
interface DifficultyLevelInfo {
  key: string;
  label: string;
  default_model: string | null;
}
interface Catalog {
  models: ModelInfo[];
  use_cases: UseCaseInfo[];
  difficulty_levels: DifficultyLevelInfo[];
  reply_tones?: { key: string; label: string }[];
  anthropic_enabled: boolean;
  gemini_enabled?: boolean;
}
interface UiPrefs {
  font_scale: FontScale;
  sources_default_state?: SourcesDefaultState;
}
interface SettingsPayload {
  models: Record<string, string>;
  difficulty_models: Record<string, string | null>;
  ui?: UiPrefs;
  mail_reply_tone?: string;
}
interface Category {
  id: string;
  name: string;
}

function fmtPrice(usd: number) {
  return `$${usd.toFixed(2)}/1M`;
}

const DIFFICULTY_SYNTH_DEFAULT = "__synth_default__";

type TabKey =
  | "account"
  | "display"
  | "mail"
  | "calendar"
  | "llm"
  | "external"
  | "admin";

const TABS: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: "account", label: "Konto", icon: <UserIcon className="w-4 h-4" /> },
  { key: "display", label: "Anzeige", icon: <Monitor className="w-4 h-4" /> },
  { key: "mail", label: "E-Mail", icon: <MailIcon className="w-4 h-4" /> },
  { key: "calendar", label: "Kalender", icon: <CalendarClock className="w-4 h-4" /> },
  { key: "llm", label: "KI-Modelle", icon: <Cpu className="w-4 h-4" /> },
  { key: "external", label: "Integrationen", icon: <Plug className="w-4 h-4" /> },
];

/** Der Admin-Reiter steht bewusst am ENDE der Liste und nicht mitten drin: er
 *  erscheint erst, wenn `/auth/me` die Rolle bestätigt hat (studioAccess.ts).
 *  Was nachträglich dazukommt, darf nichts verschieben, was schon gerendert
 *  war — dieselbe Regel wie bei den Studio-Zielen in der Navigation. */
const ADMIN_TAB: { key: TabKey; label: string; icon: React.ReactNode } = {
  key: "admin",
  label: "Admin",
  icon: <ShieldCheck className="w-4 h-4" />,
};

export default function SettingsPage() {
  const toast = useToast();
  // `st` statt `t`: die Reiterzeile unten bindet `t` schon als Schleifen-
  // Variable (`sichtbareTabs.map((t) => …)`).
  const st = useT().settings;
  const { setScale } = useFontScale();
  const { setState: setSourcesPref } = useSourcesPref();
  const istAdmin = useIsAdmin();
  const sichtbareTabs = istAdmin ? [...TABS, ADMIN_TAB] : TABS;
  const [tab, setTab] = useState<TabKey>("account");
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [models, setModels] = useState<Record<string, string>>({});
  const [difficultyModels, setDifficultyModels] = useState<Record<string, string | null>>({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const [cats, setCats] = useState<Category[]>([]);
  const [newCat, setNewCat] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [cat, mine, cs] = await Promise.all([
          api.get<Catalog>("/settings/catalog"),
          api.get<SettingsPayload>("/settings"),
          api.get<Category[]>("/categories"),
        ]);
        setCatalog(cat);
        setModels(mine.models);
        setDifficultyModels(mine.difficulty_models ?? {});
        setCats(cs);
        const serverScale = mine.ui?.font_scale;
        if (serverScale === "s" || serverScale === "m" || serverScale === "l" || serverScale === "xl") {
          setScale(serverScale);
        }
        const serverSrcPref = mine.ui?.sources_default_state;
        if (
          serverSrcPref === "collapsed" ||
          serverSrcPref === "titles" ||
          serverSrcPref === "expanded"
        ) {
          setSourcesPref(serverSrcPref);
        }
      } catch (e) {
        toast.err("Settings konnten nicht geladen werden", (e as Error).message);
      }
    })();
  }, [setScale, setSourcesPref]);

  const setModel = (useCase: string, model: string) => {
    setModels((m) => ({ ...m, [useCase]: model }));
    setDirty(true);
  };

  const setDifficultyModel = (level: string, value: string) => {
    setDifficultyModels((m) => ({
      ...m,
      [level]: value === DIFFICULTY_SYNTH_DEFAULT ? null : value,
    }));
    setDirty(true);
  };

  const saveModels = async () => {
    setSaving(true);
    try {
      const out = await api.put<SettingsPayload>("/settings", {
        models,
        difficulty_models: difficultyModels,
      });
      setModels(out.models);
      setDifficultyModels(out.difficulty_models ?? {});
      setDirty(false);
      toast.ok("Modell-Einstellungen gespeichert");
    } catch (e) {
      toast.err("Speichern fehlgeschlagen", (e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (!catalog) {
    return (
      <div className="flex items-center gap-2 p-6 text-text-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Lade …
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold">{st.kopfTitel}</h1>
        {/* Diese Zeile zählt die Tabs auf — sie muss also mitwandern, wenn einer
            dazukommt. Ein Untertitel, der einen vorhandenen Bereich verschweigt,
            ist genau die Sorte Halbwahrheit wie „20 freie Zeitfenster" über
            zwölf Karten. */}
        <p className="text-sm text-text-2 mt-1">{st.kopfUntertitel(istAdmin)}</p>
      </header>

      <div className="flex flex-col md:flex-row gap-4 md:gap-6">
        <nav className="w-full md:w-48 shrink-0">
          <ul className="flex md:block gap-1 md:space-y-1 md:sticky md:top-4 overflow-x-auto md:overflow-visible -mx-1 px-1 pb-1 md:pb-0">
            {sichtbareTabs.map((t) => (
              <li key={t.key} className="shrink-0 md:shrink">
                <button
                  onClick={() => setTab(t.key)}
                  className={clsx(
                    "w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors whitespace-nowrap",
                    tab === t.key
                      ? "bg-brand-50 dark:bg-ink-700 text-brand-700 dark:text-accent-300 font-medium"
                      : "text-text-2 hover:bg-surface-3"
                  )}
                >
                  <span className={tab === t.key ? "text-brand-500" : "text-text-3"}>
                    {t.icon}
                  </span>
                  {t.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="flex-1 min-w-0">
          {tab === "account" && (
            <AccountSection
              cats={cats}
              setCats={setCats}
              newCat={newCat}
              setNewCat={setNewCat}
              toast={toast}
            />
          )}
          {tab === "display" && <DisplaySection />}
          {tab === "mail" && <MailSection toast={toast} />}
          {tab === "calendar" && <CalendarSection toast={toast} />}
          {tab === "llm" && (
            <LlmSection
              catalog={catalog}
              models={models}
              setModel={setModel}
              difficultyModels={difficultyModels}
              setDifficultyModel={setDifficultyModel}
              dirty={dirty}
              saving={saving}
              save={saveModels}
            />
          )}
          {tab === "external" && <ExternalSection catalog={catalog} />}
          {/* `istAdmin &&` ist nicht doppelt gemoppelt: verliert die Sitzung die
              bestätigte Rolle (Token-Wechsel, Serverwackler), verschwindet auch
              der schon geöffnete Inhalt — nicht nur der Knopf dazu. */}
          {istAdmin && tab === "admin" && <AdminSection toast={toast} />}
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ E-Mail
//
// Behälter für Mail-Einstellungen. Bewusst als Liste von <section>-Karten
// aufgebaut: eine weitere Mail-Einstellung wird schlicht eine zweite Karte
// hier drunter — ohne Umbau am Tab.

function MailSection({ toast }: { toast: ReturnType<typeof useToast> }) {
  return (
    <div className="space-y-10" data-settings-mail-section>
      <MailInboxDepthCard toast={toast} />
      <MailAutoMarkReadCard toast={toast} />
      <MailReplyToneCard toast={toast} />
    </div>
  );
}

/** „Beim Öffnen als gelesen markieren" (F-06) — NUTZER-Einstellung, Default an.
 *  Gleiche Bauform und gleiche Quelle wie die Tiefen-Karte darüber
 *  (`user_settings.mail_auto_mark_read` via PUT /settings, ui/mailAutoMarkRead.ts) —
 *  keine zweite Einstellungs-Infrastruktur. */
function MailAutoMarkReadCard({ toast }: { toast: ReturnType<typeof useToast> }) {
  const { autoMarkRead, ready, saving, setAutoMarkRead } = useAutoMarkRead();

  const toggle = async () => {
    const next = !autoMarkRead;
    if (await setAutoMarkRead(next))
      toast.ok(next ? "Wird beim Öffnen als gelesen markiert" : "Bleibt beim Öffnen ungelesen");
    else toast.err("Speichern fehlgeschlagen");
  };

  return (
    <section>
      <h2 className="text-sm font-semibold uppercase tracking-wider text-text-2 mb-1">
        Beim Öffnen als gelesen markieren
      </h2>
      <p className="text-sm text-text-3 mb-3">
        Ist das an, gilt eine geöffnete Mail sofort als gelesen — auch in
        Outlook, denn Atlas schreibt den Status zurück. Aus heisst: der
        Lese-Status ändert sich nur, wenn du ihn selbst setzt (Punkt links in
        der Zeile). Die Einstellung hängt an deinem Konto und gilt darum auf
        allen Geräten.
      </p>
      <button
        type="button"
        role="switch"
        data-settings-auto-mark-read
        aria-checked={autoMarkRead}
        disabled={!ready || saving}
        onClick={() => void toggle()}
        className="flex w-full items-center gap-3 rounded-lg border border-border bg-surface px-3 py-3 text-left transition-colors hover:border-accent-300 disabled:opacity-60"
      >
        <span
          className={clsx(
            "relative h-5 w-9 shrink-0 rounded-full transition-colors",
            autoMarkRead ? "bg-accent-500" : "bg-surface-3 border border-border"
          )}
        >
          <span
            className={clsx(
              "absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform",
              autoMarkRead && "translate-x-4"
            )}
          />
        </span>
        <span className="text-sm text-text">
          Geöffnete Mails als gelesen markieren
        </span>
        {/* Der Zustand steht ausgeschrieben daneben, damit „an oder aus" nicht
            allein an der Schalter-Farbe hängt. Ganz rechts (ml-auto) und damit
            am Rand: dass „An" und „Aus" verschieden breit sind, verschiebt so
            nichts anderes in der Karte. */}
        <span className="ml-auto shrink-0 text-xs font-medium text-text-2">
          {autoMarkRead ? "An" : "Aus"}
        </span>
      </button>
    </section>
  );
}

/** Standard-Tiefe der Mail-Listen — NUTZER-Einstellung (gilt geräteübergreifend,
 *  Persistenz `user_settings.mail_inbox_depth` via PUT /settings). Dieselbe
 *  Quelle, aus der das Mail-Cockpit seine Tiefe zieht (ui/mailInboxDepth.ts) —
 *  keine zweite Einstellungs-Infrastruktur. */
function MailInboxDepthCard({ toast }: { toast: ReturnType<typeof useToast> }) {
  const { depth, ready, saving, setDepth } = useInboxDepth();
  // Beschriftung der drei Stufen aus dem Mail-Katalog — dieselbe Quelle wie im
  // Cockpit. Der Rest dieser Karte ist noch deutsch (Reiter „E-Mail" liegt
  // ausserhalb des Uebersetzungs-Scopes, docs/i18n.md §2).
  const tiefe = useT().mail.seite.tiefe;

  const choose = async (d: InboxDepth) => {
    if (d === depth) return;
    if (await setDepth(d)) toast.ok("Standard-Tiefe gespeichert");
    else toast.err("Speichern fehlgeschlagen");
  };

  return (
    <section>
      <h2 className="text-sm font-semibold uppercase tracking-wider text-text-2 mb-1">
        Standard-Tiefe der Mail-Listen
      </h2>
      <p className="text-sm text-text-3 mb-3">
        Wie tief Posteingang, Gesendet und Entwürfe geladen werden. Die
        Einstellung hängt an deinem Konto und gilt darum auf allen Geräten. Im
        Mail-Cockpit lässt sie sich jederzeit umstellen — der hier gewählte Wert
        ist der Standard. Entwürfe kennen keinen Zeitraum, dort wirkt nur die
        Anzahl.
      </p>
      <div
        data-settings-inbox-depth
        role="group"
        aria-label="Standard-Tiefe der Mail-Listen"
        className="grid gap-2 sm:grid-cols-3"
      >
        {INBOX_DEPTH_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            data-depth={opt.value}
            disabled={!ready || saving}
            aria-pressed={depth === opt.value}
            onClick={() => void choose(opt.value)}
            className={clsx(
              "rounded-lg border px-3 py-2 text-left transition-colors disabled:opacity-60",
              depth === opt.value
                ? "border-accent-400 bg-accent-50 text-text"
                : "border-border text-text-2 hover:border-accent-300 hover:text-text"
            )}
          >
            <span className="block text-sm font-medium">{tiefe[opt.value].label}</span>
            <span className="mt-0.5 block text-xs text-text-3">
              {tiefe[opt.value].hinweis}
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- Kalender
//
// Eigener Tab statt einer weiteren Karte unter „E-Mail": die Geschäftszeiten
// steuern die Terminfindung, nicht das Postfach. Aufgebaut wie `MailSection` —
// eine weitere Kalender-Einstellung wird schlicht eine zweite Karte hier
// drunter.

function CalendarSection({ toast }: { toast: ReturnType<typeof useToast> }) {
  return (
    <div className="space-y-10">
      <BusinessHoursCard toast={toast} />
    </div>
  );
}

/** W9 — frühester Beginn / spätestes Ende eines VORGESCHLAGENEN Termins.
 *
 *  NUTZER-Einstellung (gilt geräteübergreifend, Persistenz
 *  `user_settings.calendar_business_hours` via PUT /settings) — derselbe Weg wie
 *  die Mail-Tiefe darüber, keine zweite Einstellungs-Infrastruktur.
 *
 *  Sie wirkt NUR auf die Vorschläge. Einen Termin von Hand auf 07:00 legen geht
 *  weiter, und eine vom Kunden genannte Zeit außerhalb dieses Fensters wird im
 *  Wizard trotzdem angenommen (W11). Das steht auch so da: eine Einstellung, die
 *  strenger klingt, als sie ist, erzeugt genau die Anrufe, die sie sparen soll. */
function BusinessHoursCard({ toast }: { toast: ReturnType<typeof useToast> }) {
  const { hours, ready, saving, setHours } = useBusinessHours();

  const choose = async (next: { start_hour: number; end_hour: number }) => {
    if (next.start_hour === hours.start_hour && next.end_hour === hours.end_hour)
      return;
    if (next.end_hour <= next.start_hour) {
      // Kein stiller Fehlschlag: der Server lehnt das mit 422 ab, aber der
      // Nutzer sähe nur, dass „nichts passiert".
      toast.err(
        "Das Ende muss nach dem Beginn liegen",
        `${hourLabel(next.start_hour)}–${hourLabel(next.end_hour)} ist kein Zeitraum.`
      );
      return;
    }
    if (await setHours(next)) toast.ok("Geschäftszeiten gespeichert");
    else toast.err("Speichern fehlgeschlagen");
  };

  return (
    <section>
      <h2 className="text-sm font-semibold uppercase tracking-wider text-text-2 mb-1">
        Geschäftszeiten für Terminvorschläge
      </h2>
      <p className="text-sm text-text-3 mb-3">
        In welchem Zeitraum „Termin finden" nach freien Zeiten sucht. Vorschläge
        beginnen nie davor und enden nie danach. Die Einstellung hängt an deinem
        Konto und gilt darum auf allen Geräten. Sie betrifft{" "}
        <span className="font-medium">nur die Vorschläge</span>: einen Termin von
        Hand anlegen und eine vom Kunden genannte Zeit eintragen geht weiterhin
        zu jeder Uhrzeit.
      </p>
      <div
        data-settings-business-hours
        data-hours={`${hours.start_hour}-${hours.end_hour}`}
        role="group"
        aria-label="Geschäftszeiten für Terminvorschläge"
        className="flex flex-wrap items-end gap-3"
      >
        <label className="flex flex-col gap-1">
          <span className="text-xs text-text-3">Frühester Beginn</span>
          <select
            data-biz-start
            value={hours.start_hour}
            disabled={!ready || saving}
            onChange={(e) =>
              void choose({
                start_hour: Number(e.target.value),
                end_hour: hours.end_hour,
              })
            }
            className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text disabled:opacity-60"
          >
            {START_HOUR_CHOICES.map((h) => (
              <option key={h} value={h}>
                {hourLabel(h)}
              </option>
            ))}
          </select>
        </label>
        <span className="pb-2.5 text-text-3">–</span>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-text-3">Spätestes Ende</span>
          <select
            data-biz-end
            value={hours.end_hour}
            disabled={!ready || saving}
            onChange={(e) =>
              void choose({
                start_hour: hours.start_hour,
                end_hour: Number(e.target.value),
              })
            }
            className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text disabled:opacity-60"
          >
            {END_HOUR_CHOICES.map((h) => (
              <option key={h} value={h}>
                {hourLabel(h)}
              </option>
            ))}
          </select>
        </label>
      </div>
    </section>
  );
}

// -------------------------------------------------- Mail-Antwort-Ton (Default)

function MailReplyToneCard({ toast }: { toast: ReturnType<typeof useToast> }) {
  const [tones, setTones] = useState<{ key: string; label: string }[]>([]);
  const [tone, setTone] = useState<string>("standard");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get<Catalog>("/settings/catalog"),
      api.get<SettingsPayload>("/settings"),
    ])
      .then(([cat, mine]) => {
        if (!alive) return;
        setTones(cat.reply_tones ?? []);
        setTone(mine.mail_reply_tone ?? "standard");
      })
      .catch(() => {
        /* best-effort */
      });
    return () => {
      alive = false;
    };
  }, []);

  const choose = async (t: string) => {
    const prev = tone;
    setTone(t);
    setSaving(true);
    try {
      await api.put("/settings", { mail_reply_tone: t });
      toast.ok("Antwort-Ton gespeichert");
    } catch {
      setTone(prev);
      toast.err("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  if (tones.length === 0) return null;

  return (
    <section>
      <h2 className="text-sm font-semibold uppercase tracking-wider text-text-2 mb-1">
        Mail-Antwortvorschläge
      </h2>
      <p className="text-sm text-text-3 mb-3">
        Standard-Ton für die KI-Antwortvorschläge im Mail-Cockpit. Es wird ein
        Vorschlag in der Sprache der eingehenden Mail erzeugt; die Signatur wird
        automatisch angehängt.
      </p>
      <div className="flex flex-wrap gap-2">
        {tones.map((t) => (
          <button
            key={t.key}
            type="button"
            disabled={saving}
            onClick={() => void choose(t.key)}
            className={clsx(
              "rounded-lg border px-3 py-1.5 text-sm transition-colors disabled:opacity-60",
              tone === t.key
                ? "border-accent-400 bg-accent-50 text-text"
                : "border-border text-text-2 hover:border-accent-300 hover:text-text"
            )}
          >
            {t.label}
          </button>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- Account

function AccountSection({
  cats,
  setCats,
  newCat,
  setNewCat,
  toast,
}: {
  cats: Category[];
  setCats: (c: Category[]) => void;
  newCat: string;
  setNewCat: (s: string) => void;
  toast: ReturnType<typeof useToast>;
}) {
  const { username, logout } = useAuth();
  const navigate = useNavigate();

  const addCat = async () => {
    const name = newCat.trim();
    if (!name) return;
    try {
      const created = await api.post<Category>("/categories", { name });
      setCats([...cats, created].sort((a, b) => a.name.localeCompare(b.name)));
      setNewCat("");
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        toast.err("Kategorie existiert bereits");
      } else {
        toast.err("Anlegen fehlgeschlagen", (e as Error).message);
      }
    }
  };

  const renameCat = async (id: string, name: string) => {
    if (!name.trim()) return;
    try {
      const upd = await api.patch<Category>(`/categories/${id}`, { name: name.trim() });
      setCats(cats.map((c) => (c.id === id ? upd : c)).sort((a, b) => a.name.localeCompare(b.name)));
    } catch (e) {
      toast.err("Umbenennen fehlgeschlagen", (e as Error).message);
    }
  };

  const delCat = async (id: string) => {
    try {
      await api.delete(`/categories/${id}`);
      setCats(cats.filter((c) => c.id !== id));
    } catch (e) {
      toast.err("Löschen fehlgeschlagen", (e as Error).message);
    }
  };

  return (
    <div className="space-y-8">
      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-text-2 mb-3">
          Profil
        </h2>
        <div className="bg-surface border border-border rounded-lg p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-brand-500 grid place-items-center text-white text-lg font-bold shrink-0">
            {(username ?? "?").charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-base font-medium truncate">{username ?? "—"}</div>
            <div className="text-xs text-text-2">Eingeloggt</div>
          </div>
          <button
            onClick={() => {
              logout();
              navigate("/login");
            }}
            className="shrink-0 text-sm text-danger inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md hover:bg-danger-soft"
          >
            <LogOut className="w-4 h-4" />
            Abmelden
          </button>
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-text-2 mb-1">
          Kategorien
        </h2>
        <p className="text-xs text-text-2 mb-3">
          Für Notes (Schnellerfassung mit @kategorie). Löschen lässt den Tag-Text in
          bestehenden Notizen unverändert.
        </p>
        <div className="bg-surface border border-border rounded-lg divide-y divide-border">
          {cats.length === 0 && (
            <div className="px-3 py-4 text-sm text-text-2">
              Keine Kategorien. Lege unten welche an oder verwende @kategorie im Quick-Capture.
            </div>
          )}
          {cats.map((c) => (
            <div key={c.id} className="flex items-center gap-2 px-3 py-1.5">
              <input
                defaultValue={c.name}
                onBlur={(e) => {
                  if (e.target.value !== c.name) renameCat(c.id, e.target.value);
                }}
                className="flex-1 px-1 py-0.5 bg-transparent text-sm focus:outline-none focus:bg-surface-2 rounded-md"
              />
              <button
                onClick={() => delCat(c.id)}
                className="p-1 text-text-3 hover:text-danger"
                title="Löschen"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <div className="flex items-center gap-2 px-3 py-2 bg-surface-2/50">
            <input
              value={newCat}
              onChange={(e) => setNewCat(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addCat();
                }
              }}
              placeholder="Neue Kategorie …"
              className="flex-1 px-1 py-0.5 bg-transparent text-sm focus:outline-none"
            />
            <button
              onClick={addCat}
              disabled={!newCat.trim()}
              className="p-1 text-text-2 hover:text-brand-600 disabled:opacity-40"
              title="Hinzufügen"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------- Display

// Nur noch Wert + Icon: die Beschriftung kommt aus dem Katalog
// (`t.settings.modus[value]`), damit sie der Sprach-Einstellung folgt.
const MODE_OPTIONS: { value: Mode; icon: typeof Sun }[] = [
  { value: "light", icon: Sun },
  { value: "dark", icon: Moon },
  { value: "system", icon: Monitor },
];

const SIDEBAR_EXPANDED_KEY = "sb_sidebar_projects_expanded";

function DisplaySection() {
  const { mode, preset, setMode, setPreset, custom, setCustom } = useTheme();
  const { lang, setLang, speichert: spracheSpeichert } = useLang();
  const t = useT();
  const { scale, setScale } = useFontScale();
  const { font: uiFont, setFont: setUiFont, speichert: fontSpeichert } = useUiFont();
  const { state: sourcesPref, setState: setSourcesPref } = useSourcesPref();
  const { view: taskView, setView: setTaskView } = useTaskViewPref();
  const toast = useToast();
  const [persisting, setPersisting] = useState(false);
  const [persistingSrc, setPersistingSrc] = useState(false);
  // Schriften, die diese Sektion anbietet, die dieses GERÄT aber nicht hat
  // (praktisch: Aptos — nicht gebündelt, Microsoft-Lizenz). Gemessen beim
  // Rendern der Sektion, nicht geraten: sonst behauptete der Schalter eine
  // Wirkung, die er auf diesem Rechner nicht hat.
  const [fehlendeFonts, setFehlendeFonts] = useState<UiFont[]>([]);

  useEffect(() => {
    setFehlendeFonts(
      UI_FONT_OPTIONS.filter((o) => o.systemName && !fontInstalliert(o.systemName)).map(
        (o) => o.value,
      ),
    );
  }, []);
  const [sidebarExpanded, setSidebarExpanded] = useState(
    () => localStorage.getItem(SIDEBAR_EXPANDED_KEY) === "1",
  );

  const toggleSidebarExpanded = (v: boolean) => {
    setSidebarExpanded(v);
    localStorage.setItem(SIDEBAR_EXPANDED_KEY, v ? "1" : "0");
  };

  const patchCustom = (patch: Partial<CustomTheme>) => {
    const next = { ...(preset === "custom" ? custom : CUSTOM_DEFAULT), ...patch };
    setCustom(next);
    if (preset !== "custom") setPreset("custom");
  };

  // Die Sprache schaltet sofort um (der Provider schreibt sie in denselben
  // Speicher, aus dem alle Beschriftungen lesen) und wird danach am Nutzer
  // gespeichert. Für die Fehlermeldung wird der Katalog NACH dem Umschalten
  // geholt (`katalog()` statt des `t` aus diesem Render) — sonst käme die
  // Meldung in der Sprache, die der Nutzer gerade verlassen hat.
  const pickLang = async (l: Lang) => {
    try {
      await setLang(l);
    } catch (e) {
      toast.err(katalog().settings.spracheFehler, (e as Error).message);
    }
  };

  const pickScale = async (s: FontScale) => {
    setScale(s);
    setPersisting(true);
    try {
      await api.put("/settings", { ui: { font_scale: s, sources_default_state: sourcesPref } });
    } catch (e) {
      toast.err(t.settings.schriftgroesseFehler, (e as Error).message);
    } finally {
      setPersisting(false);
    }
  };

  // Der Provider schaltet sofort um und speichert danach am Nutzer (dieselbe
  // Form wie `pickLang`). Scheitert der PUT, bleibt die neue Schrift stehen —
  // gesagt wird trotzdem, dass sie nicht gespeichert wurde.
  const pickFont = async (f: UiFont) => {
    try {
      await setUiFont(f);
    } catch (e) {
      toast.err(t.settings.schriftartFehler, (e as Error).message);
    }
  };

  const pickSourcesPref = async (v: SourcesDefaultState) => {
    setSourcesPref(v);
    setPersistingSrc(true);
    try {
      await api.put("/settings", { ui: { font_scale: scale, sources_default_state: v } });
    } catch (e) {
      toast.err(t.settings.quellenFehler, (e as Error).message);
    } finally {
      setPersistingSrc(false);
    }
  };

  const sprachen: { value: Lang; label: string }[] = [
    { value: "de", label: t.settings.spracheDeutsch },
    { value: "en", label: t.settings.spracheEnglisch },
  ];

  // Text aus dem Katalog, Struktur (Wert, Swatches, Icon) aus den Tabellen in
  // ui/theme.tsx & Co. — dieselbe Trennung wie in ui/labels.ts. „Eigenes" ist
  // kein PRESETS-Eintrag, hat aber dieselbe Karte und deshalb eigene Einträge.
  const presetName = (id: Preset) =>
    id === "custom" ? t.settings.presetEigenes : t.settings.presetName[id];
  const presetHinweis = (id: Preset) =>
    id === "custom" ? t.settings.presetEigenesHinweis : t.settings.presetHinweis[id];

  return (
    <div className="space-y-8">
      {/* Sprache steht oben: sie entscheidet, in welcher Sprache alles darunter
          dasteht. Der Titel ist in BEIDEN Sprachen beschriftet, damit sie auch
          findet, wer die Oberfläche gerade nicht lesen kann. */}
      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-text-2 mb-1">
          {t.settings.spracheTitel}
        </h2>
        <p className="text-xs text-text-2 mb-3">
          {t.settings.spracheHinweis}
          {spracheSpeichert && t.settings.spracheSpeichert}
        </p>
        <div className="grid grid-cols-2 gap-2">
          {sprachen.map((opt) => {
            const active = lang === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => pickLang(opt.value)}
                aria-pressed={active}
                className={clsx(
                  "border rounded-lg px-3 py-3 text-left transition-colors",
                  active
                    ? "border-brand-500 bg-brand-50 dark:bg-ink-700"
                    : "border-border hover:border-border-strong"
                )}
              >
                <div className="text-sm font-medium flex items-center gap-1.5">
                  {opt.label}
                  {active && <Check className="w-3.5 h-3.5 text-brand-500" />}
                </div>
                <div className="text-[11px] text-text-2 mt-0.5 uppercase tracking-wider">
                  {opt.value}
                </div>
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-text-3 mt-2 leading-snug">
          {t.settings.spracheHinweisMail} {t.settings.spracheHinweisDatum}
        </p>
      </section>

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-text-2 mb-1">
          {t.settings.erscheinungsbildTitel}
        </h2>
        <p className="text-xs text-text-2 mb-3">{t.settings.erscheinungsbildHinweis}</p>
        <div className="grid grid-cols-2 gap-3 mb-4">
          {PRESETS.map((p) => {
            const active = preset === p.id;
            return (
              <button
                key={p.id}
                onClick={() => setPreset(p.id)}
                aria-pressed={active}
                className={clsx(
                  "border rounded-lg p-4 text-left transition-colors",
                  active
                    ? "border-brand-500 ring-2 ring-brand-500/40 bg-brand-50 dark:bg-ink-700"
                    : "border-border hover:border-border-strong"
                )}
              >
                <div className="flex items-center gap-1.5 mb-2.5">
                  {p.swatches.map((c) => (
                    <span
                      key={c}
                      className="w-4 h-4 rounded-full border border-black/10 dark:border-white/15"
                      style={{ background: c }}
                    />
                  ))}
                </div>
                <div className="text-sm font-medium flex items-center gap-1.5">
                  {presetName(p.id)}
                  {active && <Check className="w-3.5 h-3.5 text-brand-500" />}
                </div>
                <div className="text-xs text-text-2 mt-0.5">{presetHinweis(p.id)}</div>
              </button>
            );
          })}
          {/* Eigenes Preset */}
          {(() => {
            const active = preset === "custom";
            const canvasSwatch = custom.canvasTint > 0 ? "#fafaf8" : "#ffffff";
            return (
              <button
                key="custom"
                onClick={() => setPreset("custom")}
                aria-pressed={active}
                className={clsx(
                  "border rounded-lg p-4 text-left transition-colors",
                  active
                    ? "border-brand-500 ring-2 ring-brand-500/40 bg-brand-50 dark:bg-ink-700"
                    : "border-border hover:border-border-strong"
                )}
              >
                <div className="flex items-center gap-1.5 mb-2.5">
                  {[canvasSwatch, custom.accent, "#1a1d1f"].map((c, i) => (
                    <span
                      key={i}
                      className="w-4 h-4 rounded-full border border-black/10 dark:border-white/15"
                      style={{ background: c }}
                    />
                  ))}
                </div>
                <div className="text-sm font-medium flex items-center gap-1.5">
                  {presetName("custom")}
                  {active && <Check className="w-3.5 h-3.5 text-brand-500" />}
                </div>
                <div className="text-xs text-text-2 mt-0.5">{presetHinweis("custom")}</div>
              </button>
            );
          })()}
        </div>

        {preset === "custom" && (
          <div className="border border-border rounded-lg p-4 mb-4 space-y-4 bg-surface-2/40">
            {/* Akzentfarbe */}
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium">{t.settings.akzentfarbe}</div>
                <div className="text-xs text-text-2">{t.settings.akzentfarbeHinweis}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono text-text-2 uppercase">{custom.accent}</span>
                <input
                  type="color"
                  value={custom.accent}
                  onChange={(e) => patchCustom({ accent: e.target.value })}
                  className="w-10 h-8 rounded-md border border-border bg-transparent cursor-pointer p-0.5"
                  aria-label={t.settings.akzentfarbe}
                />
              </div>
            </div>

            {/* Canvas-Ton */}
            <div>
              <div className="text-sm font-medium mb-1.5">{t.settings.canvasTon}</div>
              <div className="inline-flex rounded-md border border-border overflow-hidden">
                {[
                  { v: 0, label: t.settings.canvasReinweiss },
                  { v: 1, label: t.settings.canvasGetoent },
                ].map((o) => {
                  const on = (custom.canvasTint > 0 ? 1 : 0) === o.v;
                  return (
                    <button
                      key={o.v}
                      onClick={() => patchCustom({ canvasTint: o.v })}
                      aria-pressed={on}
                      className={clsx(
                        "px-3 py-1.5 text-sm transition-colors",
                        on
                          ? "bg-brand-500 text-white"
                          : "bg-surface text-text-2 hover:bg-surface-3"
                      )}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Linien */}
            <div>
              <div className="text-sm font-medium mb-1.5">{t.settings.linien}</div>
              <div className="inline-flex rounded-md border border-border overflow-hidden">
                {([
                  { v: "normal", label: t.settings.linienDezent },
                  { v: "strong", label: t.settings.linienKraeftig },
                ] as const).map((o) => {
                  const on = custom.borderStrength === o.v;
                  return (
                    <button
                      key={o.v}
                      onClick={() => patchCustom({ borderStrength: o.v })}
                      aria-pressed={on}
                      className={clsx(
                        "px-3 py-1.5 text-sm transition-colors",
                        on
                          ? "bg-brand-500 text-white"
                          : "bg-surface text-text-2 hover:bg-surface-3"
                      )}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <div className="inline-flex rounded-md border border-border overflow-hidden">
          {MODE_OPTIONS.map((opt) => {
            const active = mode === opt.value;
            const Icon = opt.icon;
            return (
              <button
                key={opt.value}
                onClick={() => setMode(opt.value)}
                aria-pressed={active}
                className={clsx(
                  "inline-flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors",
                  active
                    ? "bg-brand-500 text-white"
                    : "bg-surface text-text-2 hover:bg-surface-3"
                )}
              >
                <Icon className="w-4 h-4" />
                {t.settings.modus[opt.value]}
              </button>
            );
          })}
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-text-2 mb-1">
          {t.settings.schriftgroesseTitel}
        </h2>
        <p className="text-xs text-text-2 mb-3">
          {t.settings.schriftgroesseHinweis}
          {persisting && t.settings.speichert}
        </p>
        <div className="grid grid-cols-4 gap-2">
          {FONT_SCALE_OPTIONS.map((opt) => {
            const active = scale === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => pickScale(opt.value)}
                className={clsx(
                  "border rounded-lg px-3 py-3 text-center transition-colors",
                  active
                    ? "border-brand-500 bg-brand-50 dark:bg-ink-700"
                    : "border-border hover:border-border-strong"
                )}
              >
                <div
                  className="font-semibold mb-0.5"
                  style={{ fontSize: opt.sample }}
                >
                  Aa
                </div>
                <div className="text-xs text-text-2">{t.settings.schriftgroesse[opt.value]}</div>
                <div className="text-[10px] text-text-3">{opt.sample}</div>
              </button>
            );
          })}
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-text-2 mb-1">
          {t.settings.schriftartTitel}
        </h2>
        <p className="text-xs text-text-2 mb-3">
          {t.settings.schriftartHinweis}
          {fontSpeichert && t.settings.speichert}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {UI_FONT_OPTIONS.map((opt) => {
            const active = uiFont === opt.value;
            const fehlt = fehlendeFonts.includes(opt.value);
            return (
              <button
                key={opt.value}
                onClick={() => pickFont(opt.value)}
                aria-pressed={active}
                className={clsx(
                  "border rounded-lg px-3 py-3 text-left transition-colors",
                  active
                    ? "border-brand-500 bg-brand-50 dark:bg-ink-700"
                    : "border-border hover:border-border-strong"
                )}
              >
                {/* Live-Vorschau: der Name steht in SEINER Schrift. Der
                    x-Höhen-Anker läuft mit, damit die sieben Namen bei gleicher
                    Punktgrösse auch gleich gross DASTEHEN — sonst verglichen
                    sich hier Schriften, die die Oberfläche später normiert. */}
                <div
                  className="text-base font-medium flex items-center gap-1.5"
                  style={{
                    fontFamily: UI_FONT_STACKS[opt.value],
                    fontSizeAdjust: X_HOEHEN_ANKER,
                  }}
                  data-font-vorschau={opt.value}
                >
                  <span className="truncate">{t.settings.schriftart[opt.value].label}</span>
                  {active && <Check className="w-3.5 h-3.5 text-brand-500 shrink-0" />}
                </div>
                <div className="text-[11px] text-text-2 mt-0.5 leading-snug">
                  {t.settings.schriftart[opt.value].hinweis}
                </div>
                {fehlt && (
                  <div className="text-[11px] text-warning mt-1 leading-snug">
                    {t.settings.schriftartNichtInstalliert}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-text-2 mb-1">
          {t.settings.quellenTitel}
        </h2>
        <p className="text-xs text-text-2 mb-3">
          {t.settings.quellenHinweis}
          {persistingSrc && t.settings.speichert}
        </p>
        <div className="grid grid-cols-3 gap-2">
          {SOURCES_DEFAULT_OPTIONS.map((opt) => {
            const active = sourcesPref === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => pickSourcesPref(opt.value)}
                className={clsx(
                  "border rounded-lg px-3 py-3 text-left transition-colors",
                  active
                    ? "border-brand-500 bg-brand-50 dark:bg-ink-700"
                    : "border-border hover:border-border-strong"
                )}
              >
                <div className="text-sm font-medium">{t.settings.quellen[opt.value].label}</div>
                <div className="text-[11px] text-text-2 mt-0.5 leading-snug">
                  {t.settings.quellen[opt.value].hinweis}
                </div>
              </button>
            );
          })}
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-text-2 mb-1">
          {t.settings.aufgabenAnsichtTitel}
        </h2>
        <p className="text-xs text-text-2 mb-3">{t.settings.aufgabenAnsichtHinweis}</p>
        <div className="grid grid-cols-2 gap-2">
          {TASK_VIEW_OPTIONS.map((opt) => {
            const active = taskView === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => setTaskView(opt.value)}
                aria-pressed={active}
                className={clsx(
                  "border rounded-lg px-3 py-3 text-left transition-colors",
                  active
                    ? "border-brand-500 bg-brand-50 dark:bg-ink-700"
                    : "border-border hover:border-border-strong"
                )}
              >
                <div className="text-sm font-medium">
                  {t.settings.aufgabenAnsicht[opt.value].label}
                </div>
                <div className="text-[11px] text-text-2 mt-0.5 leading-snug">
                  {t.settings.aufgabenAnsicht[opt.value].hinweis}
                </div>
              </button>
            );
          })}
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-text-2 mb-3">
          {t.settings.seitenleisteTitel}
        </h2>
        <button
          type="button"
          role="switch"
          aria-checked={sidebarExpanded}
          onClick={() => toggleSidebarExpanded(!sidebarExpanded)}
          className="w-full flex items-center gap-3 bg-surface border border-border rounded-lg px-3 py-3 text-left hover:border-border-strong transition-colors"
        >
          <span
            className={clsx(
              "relative w-9 h-5 rounded-full transition-colors shrink-0",
              sidebarExpanded ? "bg-brand-500" : "bg-surface-3 border border-border"
            )}
          >
            <span
              className={clsx(
                "absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform",
                sidebarExpanded && "translate-x-4"
              )}
            />
          </span>
          <span className="text-sm">{t.settings.seitenleisteAusgeklappt}</span>
        </button>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------- LLM Models

function LlmSection({
  catalog,
  models,
  setModel,
  difficultyModels,
  setDifficultyModel,
  dirty,
  saving,
  save,
}: {
  catalog: Catalog;
  models: Record<string, string>;
  setModel: (useCase: string, model: string) => void;
  difficultyModels: Record<string, string | null>;
  setDifficultyModel: (level: string, value: string) => void;
  dirty: boolean;
  saving: boolean;
  save: () => void;
}) {
  const pickable = catalog.models;
  const claudeMissing = !catalog.anthropic_enabled;

  return (
    <div className="space-y-8">
      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-text-2 mb-3">
          KI-Modelle pro Anwendungsfall
        </h2>
        {claudeMissing && (
          <div className="mb-3 flex items-start gap-2 text-xs text-warning bg-warning-soft border border-warning/40 rounded-md px-3 py-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              ANTHROPIC_API_KEY ist auf dem Server nicht gesetzt. Claude-Modelle
              lassen sich speichern, fallen bei Verwendung aber auf gpt-4.1-mini
              zurück.
            </span>
          </div>
        )}
        <div className="space-y-3">
          {catalog.use_cases.map((uc) => (
            <div
              key={uc.key}
              className="flex items-center gap-3 bg-surface border border-border rounded-lg px-3 py-2"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{uc.label}</div>
                <div className="text-[11px] text-text-2 truncate">Standard: {uc.default_model}</div>
              </div>
              <select
                value={models[uc.key] ?? uc.default_model}
                onChange={(e) => setModel(uc.key, e.target.value)}
                className="min-w-0 max-w-[14rem] px-2 py-1.5 text-sm bg-surface-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/40"
              >
                {pickable.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label} · {fmtPrice(m.in_per_m_usd)} in / {fmtPrice(m.out_per_m_usd)} out
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-text-2 mb-1">
          Chat-Difficulty-Routing
        </h2>
        <p className="text-xs text-text-2 mb-3">
          Welches Modell beim Stufen-Umschalter im Chat-Composer (Schnell / Standard / Gründlich / Experte)
          tatsächlich genutzt wird. „Synthesis-Default" bedeutet: nimm das Modell aus der Use-Case-Tabelle
          oben.
        </p>
        <div className="space-y-3">
          {(catalog.difficulty_levels ?? []).map((dl) => {
            const cur = difficultyModels[dl.key];
            const selected = cur === null || cur === undefined ? DIFFICULTY_SYNTH_DEFAULT : cur;
            return (
              <div
                key={dl.key}
                className="flex items-center gap-3 bg-surface border border-border rounded-lg px-3 py-2"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{dl.label}</div>
                  <div className="text-[11px] text-text-2 truncate">
                    Standard: {dl.default_model ?? "Synthesis-Default"}
                  </div>
                </div>
                <select
                  value={selected}
                  onChange={(e) => setDifficultyModel(dl.key, e.target.value)}
                  className="min-w-0 max-w-[14rem] px-2 py-1.5 text-sm bg-surface-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                >
                  <option value={DIFFICULTY_SYNTH_DEFAULT}>Synthesis-Default</option>
                  {pickable.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.label} · {fmtPrice(m.in_per_m_usd)} in / {fmtPrice(m.out_per_m_usd)} out
                    </option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
        <div className="mt-4 flex justify-end">
          <Button
            variant="primary"
            onClick={save}
            disabled={!dirty}
            loading={saving}
            icon={<Save className="w-4 h-4" />}
          >
            Speichern
          </Button>
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------- External

function ExternalSection({ catalog }: { catalog: Catalog }) {
  const items: { label: string; ok: boolean; hint: string }[] = [
    {
      label: "OpenAI",
      ok: true,
      hint: "Embeddings (text-embedding-3-small) + GPT-Modelle. Konfiguration in .env (OPENAI_API_KEY).",
    },
    {
      label: "Anthropic (Claude)",
      ok: catalog.anthropic_enabled,
      hint: catalog.anthropic_enabled
        ? "Claude-Modelle verfügbar."
        : "ANTHROPIC_API_KEY nicht gesetzt. Claude-Auswahl fällt auf gpt-4.1-mini zurück.",
    },
    {
      label: "Google (Gemini)",
      ok: !!catalog.gemini_enabled,
      hint: catalog.gemini_enabled
        ? "Gemini-Modelle verfügbar (Default-Workhorse für synthesis)."
        : "GEMINI_API_KEY nicht gesetzt. Gemini-Auswahl fällt auf OpenAI zurück.",
    },
    {
      label: "Salesforce",
      ok: true,
      hint: "Read-only Spiegel von Project__c / Action__c. Credentials user-weit in .env.",
    },
    {
      label: "doc-ai Service",
      ok: true,
      hint: "Einziger Egress Dokument → externe KI (Mistral OCR + Gemini Vision). Container im bitop-internal Docker-Netz.",
    },
  ];

  return (
    <div className="space-y-4">
      <header>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-text-2 mb-1">
          Externe Dienste
        </h2>
        <p className="text-xs text-text-2 mb-3">
          Status der Provider-Konfiguration (auf Server-Ebene, nicht pro User).
        </p>
      </header>
      <div className="space-y-2">
        {items.map((it) => (
          <div
            key={it.label}
            className="flex items-start gap-3 bg-surface border border-border rounded-lg px-3 py-2.5"
          >
            <div
              className={clsx(
                "mt-0.5 w-5 h-5 rounded-full grid place-items-center shrink-0",
                it.ok
                  ? "bg-success-soft text-success"
                  : "bg-warning-soft text-warning"
              )}
              title={it.ok ? "Konfiguriert" : "Nicht konfiguriert"}
            >
              {it.ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{it.label}</div>
              <div className="text-xs text-text-2">{it.hint}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------- Admin
//
// Org-weite Einstellungen. Sichtbar nur mit der Atlas-Rolle `admin`
// (studioAccess.ts::useIsAdmin) — aber das ist ANZEIGE. Die Grenze steht
// serverseitig vor jedem Verb (app/roles.py::require_admin); wer die Rolle im
// Browser fälscht, bekommt einen Reiter, dessen Knöpfe 403 liefern.

/** Ein Bild des org-weiten Banners — Formularzeile und Nutzlast in einem. */
interface BannerBild {
  url: string;
  href: string | null;
  alt: string;
  width: number | null;
}

interface OrgBanner {
  images: BannerBild[] | null;
  updated_by?: string | null;
  updated_at?: string | null;
  signature_preview_html: string;
}

const LEERES_BILD: BannerBild = { url: "", href: null, alt: "", width: null };

/** Serverfehler lesbar machen. Nötig, weil `api.ts:47-50` jedes 422 zu
 *  `[object Object]` verkürzt (bekannter, hier NICHT behobener Fehler) — die
 *  Meldung „url muss mit http:// beginnen" ist genau die, die der Admin
 *  braucht. Vorbild: `fuSaveFehlerText()` in Mail.tsx. */
function bannerFehlerText(err: unknown): string {
  const e = err as ApiError;
  const detail = (e?.body as { detail?: unknown } | undefined)?.detail;
  if (Array.isArray(detail)) {
    const zeilen = detail
      .map((d) => {
        const x = d as { loc?: unknown[]; msg?: string };
        const grund = (x.msg ?? "").replace(/^Value error,\s*/, "");
        if (!grund) return null;
        const nr = (x.loc ?? []).find((p) => typeof p === "number");
        return nr == null ? grund : `Bild ${Number(nr) + 1}: ${grund}`;
      })
      .filter(Boolean) as string[];
    if (zeilen.length) return zeilen.join(" · ");
  }
  if (typeof detail === "string" && detail) return detail;
  return (e?.message as string) || "Unbekannter Fehler";
}

function AdminSection({ toast }: { toast: ReturnType<typeof useToast> }) {
  return (
    <div className="space-y-10">
      <OrgBannerCard toast={toast} />
    </div>
  );
}

/** Das eine org-weit gültige Signatur-Banner: hinterlegen, tauschen, löschen.
 *
 *  Gesetzt wird es für ALLE — deshalb steht die gerenderte Signatur daneben und
 *  nicht bloss die URL. Wer 76 fremde Mails ändert, soll vorher sehen, was
 *  drinsteht. */
function OrgBannerCard({ toast }: { toast: ReturnType<typeof useToast> }) {
  const [bilder, setBilder] = useState<BannerBild[]>([{ ...LEERES_BILD }]);
  const [gesetzt, setGesetzt] = useState(false);
  const [vorschau, setVorschau] = useState("");
  const [stand, setStand] = useState<string | null>(null);
  const [laden, setLaden] = useState(true);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const uebernehmen = (b: OrgBanner) => {
    setGesetzt(!!b.images && b.images.length > 0);
    setBilder(
      b.images && b.images.length
        ? b.images.map((i) => ({ ...i }))
        : [{ ...LEERES_BILD }]
    );
    setVorschau(b.signature_preview_html || "");
    setStand(
      b.updated_at
        ? `zuletzt geändert ${new Date(b.updated_at).toLocaleString("de-DE")}${
            b.updated_by ? ` von ${b.updated_by}` : ""
          }`
        : null
    );
  };

  useEffect(() => {
    let weg = false;
    api
      .get<OrgBanner>("/settings/org/banner")
      .then((b) => {
        if (!weg) uebernehmen(b);
      })
      .catch((e) => {
        if (!weg) setFehler(bannerFehlerText(e));
      })
      .finally(() => {
        if (!weg) setLaden(false);
      });
    return () => {
      weg = true;
    };
  }, []);

  const aendern = (i: number, feld: keyof BannerBild, wert: string) =>
    setBilder((alt) =>
      alt.map((b, k) =>
        k !== i
          ? b
          : feld === "width"
            ? { ...b, width: wert.trim() ? Number(wert) : null }
            : feld === "href"
              ? { ...b, href: wert.trim() || null }
              : { ...b, [feld]: wert }
      )
    );

  const speichern = async () => {
    setBusy(true);
    setFehler(null);
    try {
      const nutzlast = bilder
        .filter((b) => b.url.trim())
        .map((b) => ({
          url: b.url.trim(),
          href: b.href?.trim() || null,
          alt: b.alt ?? "",
          width: b.width ?? null,
        }));
      if (!nutzlast.length) {
        // Nicht als 422 vom Server holen: „kein Banner" ist der Löschen-Knopf,
        // nicht ein Speichern ohne Inhalt. Zwei Wege in denselben Zustand wären
        // genau die Unschärfe, die die Tabelle (genau eine Zeile oder keine)
        // vermeidet.
        setFehler(
          "Mindestens ein Bild mit Adresse — kein Banner? Dann auf „Banner entfernen“ klicken."
        );
        return;
      }
      uebernehmen(
        await api.put<OrgBanner>("/settings/org/banner", { images: nutzlast })
      );
      toast.ok("Banner gilt jetzt org-weit");
    } catch (e) {
      setFehler(bannerFehlerText(e));
      toast.err("Banner konnte nicht gesetzt werden");
    } finally {
      setBusy(false);
    }
  };

  const entfernen = async () => {
    setBusy(true);
    setFehler(null);
    try {
      await api.delete("/settings/org/banner");
      uebernehmen(await api.get<OrgBanner>("/settings/org/banner"));
      toast.ok("Banner entfernt — die Signatur trägt wieder das Logo");
    } catch (e) {
      setFehler(bannerFehlerText(e));
      toast.err("Banner konnte nicht entfernt werden");
    } finally {
      setBusy(false);
    }
  };

  if (laden) {
    return (
      <div className="flex items-center gap-2 text-text-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Lade …
      </div>
    );
  }

  return (
    <section data-admin-banner data-admin-banner-gesetzt={gesetzt ? "1" : "0"}>
      <h2 className="text-sm font-semibold uppercase tracking-wider text-text-2 mb-1">
        Signatur-Banner (org-weit)
      </h2>
      <p className="text-sm text-text-3 mb-3">
        Das hier hinterlegte Banner erscheint in der Signatur <strong>aller</strong>{" "}
        Mitarbeiter — an der Stelle, an der sonst das bitop-Logo steht. Ist keins
        hinterlegt, steht dort wieder das Logo. Bilder bitte als Adresse unter{" "}
        <code>bitop.de/gfx/…</code> angeben und nicht einbetten: eingebettete
        Bilder zeigt Outlook unzuverlässig, und sie hingen in jeder einzelnen Mail
        mit.
      </p>

      {fehler && (
        <div
          data-admin-banner-fehler
          className="mb-3 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2 text-sm text-text"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-warning" />
          <span>{fehler}</span>
        </div>
      )}

      <div className="space-y-3">
        {bilder.map((b, i) => (
          <div
            key={i}
            className="rounded-lg border border-border bg-surface p-3 space-y-2"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wider text-text-3">
                Bild {i + 1}
              </span>
              {bilder.length > 1 && (
                <button
                  type="button"
                  data-admin-banner-bild-weg={i}
                  onClick={() => setBilder((a) => a.filter((_, k) => k !== i))}
                  className="text-text-3 hover:text-warning"
                  aria-label={`Bild ${i + 1} entfernen`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
            <input
              data-admin-banner-url={i}
              value={b.url}
              onChange={(e) => aendern(i, "url", e.target.value)}
              placeholder="https://bitop.de/gfx/banner-cphi-milan.png"
              className="w-full rounded-md border border-border bg-surface-2 px-3 py-2 text-sm"
              aria-label={`Bild-Adresse ${i + 1}`}
            />
            <div className="grid gap-2 sm:grid-cols-3">
              <input
                data-admin-banner-href={i}
                value={b.href ?? ""}
                onChange={(e) => aendern(i, "href", e.target.value)}
                placeholder="Ziel-Link (optional)"
                className="rounded-md border border-border bg-surface-2 px-3 py-2 text-sm"
                aria-label={`Ziel-Link ${i + 1}`}
              />
              <input
                data-admin-banner-alt={i}
                value={b.alt}
                onChange={(e) => aendern(i, "alt", e.target.value)}
                placeholder="Alt-Text"
                className="rounded-md border border-border bg-surface-2 px-3 py-2 text-sm"
                aria-label={`Alt-Text ${i + 1}`}
              />
              <input
                data-admin-banner-width={i}
                value={b.width ?? ""}
                onChange={(e) => aendern(i, "width", e.target.value)}
                placeholder="Breite in px"
                inputMode="numeric"
                className="rounded-md border border-border bg-surface-2 px-3 py-2 text-sm"
                aria-label={`Breite ${i + 1}`}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {bilder.length < 4 && (
          <Button
            variant="ghost"
            data-admin-banner-bild-plus
            onClick={() => setBilder((a) => [...a, { ...LEERES_BILD }])}
          >
            <Plus className="w-4 h-4" /> Weiteres Bild
          </Button>
        )}
        <Button data-admin-banner-save disabled={busy} onClick={() => void speichern()}>
          {busy ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Banner setzen
        </Button>
        {gesetzt && (
          <Button
            variant="ghost"
            data-admin-banner-delete
            disabled={busy}
            onClick={() => void entfernen()}
          >
            <Trash2 className="w-4 h-4" /> Banner entfernen
          </Button>
        )}
        {stand && <span className="text-xs text-text-3">{stand}</span>}
      </div>

      <h3 className="mt-6 mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-text-2">
        <ImageIcon className="w-4 h-4" /> So sieht deine Signatur damit aus
      </h3>
      <div
        data-admin-banner-preview
        className="rounded-lg border border-border bg-white p-4 text-black overflow-x-auto"
        // Serverseitig gebautes HTML aus `app/mail/signature.py` — dieselbe
        // Zeichenkette, die auch in die Mail geht. Die variablen Teile sind dort
        // escaped; hier wird nichts zusammengesetzt.
        dangerouslySetInnerHTML={{ __html: vorschau }}
      />
    </section>
  );
}
